import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDownRight, ShieldCheck } from "lucide-react";
import { BookButton } from "./BookButton";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxkYXRhY2VudGVyJTIwbmV0d29ya3xlbnwwfHx8fDE3OTA4NjM2NDV8MA&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "Architecting", cls: "text-slate-50" },
  { text: "Unbreachable", cls: "text-gradient" },
  { text: "Digital Infrastructure", cls: "text-slate-50" },
];

const CHIPS = ["Zero Trust", "MITRE ATT&CK", "SIEM / XDR", "SOAR"];

const NODES = [
  { top: "18%", left: "8%", size: 6, delay: 0 },
  { top: "62%", left: "4%", size: 4, delay: 1.4 },
  { top: "30%", left: "88%", size: 5, delay: 0.7 },
  { top: "76%", left: "82%", size: 6, delay: 2.1 },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const panelY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const glowO = useTransform(scrollYProgress, [0, 1], [1, 0.15]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), {
    stiffness: 120,
    damping: 18,
  });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), {
    stiffness: 120,
    damping: 18,
  });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-32"
    >
      {/* Parallax background layers */}
      <motion.div
        style={{ y: bgY }}
        className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_75%_65%_at_50%_35%,black,transparent)]"
      />
      <motion.div style={{ opacity: glowO }} className="absolute inset-0">
        <div className="absolute -top-40 left-[8%] h-[480px] w-[480px] rounded-full bg-cyan-500/15 blur-[140px]" />
        <div className="absolute bottom-[-160px] right-[6%] h-[420px] w-[420px] rounded-full bg-emerald-500/12 blur-[140px]" />
      </motion.div>
      {NODES.map((n, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-cyan-300 shadow-[0_0_16px_rgba(0,240,255,0.9)]"
          style={{ top: n.top, left: n.left, width: n.size, height: n.size }}
          animate={{ opacity: [0.25, 1, 0.25], y: [0, -10, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, delay: n.delay }}
        />
      ))}

      <div className="container relative z-10 grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
        {/* Copy */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.32em] text-cyan-300/90"
          >
            <span className="h-px w-9 bg-cyan-400/60" />
            GentecIT — IT &amp; Cybersecurity Consultancy
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-7 font-display text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {LINES.map((l, i) => (
              <span key={l.text} className="-mb-1 block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 0.95,
                    delay: 0.25 + i * 0.13,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
          >
            Enterprise-grade cybersecurity consulting, precision use case
            engineering, and automated response playbooks — built for modern
            threat surfaces.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <BookButton testid="hero-cta-book" className="px-7 py-3.5" />
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollToSection("#services")}
              className="group flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-display text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-cyan-300/50 hover:text-cyan-300"
            >
              Explore Services
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-2.5"
          >
            {CHIPS.map((c) => (
              <span
                key={c}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400"
              >
                <ShieldCheck className="h-3 w-3 text-emerald-400/80" />
                {c}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Treated ops image panel with 3D tilt */}
        <motion.div
          style={{ y: panelY }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div style={{ perspective: 1200 }} className="relative">
            <motion.div
              data-testid="hero-image"
              style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
              className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_rgba(0,240,255,0.09)]"
            >
              <img
                src={HERO_IMG}
                alt="Security operations center monitoring infrastructure"
                className="aspect-[4/5] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]"
              />
              {/* Spotlight + tone */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_25%_15%,rgba(0,240,255,0.16),transparent_55%)]" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl" />
            </motion.div>

            {/* Floating status badge */}
            <div className="absolute -bottom-6 -left-3 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A111F]/90 px-5 py-4 shadow-2xl backdrop-blur-xl sm:-left-8">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
              </span>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                  Response pipeline
                </p>
                <p className="font-display text-sm font-bold text-slate-100">
                  Armed &amp; Automated
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
